/* ═══════════════════════════════════════════════════════════════════════════
 * PARAMÉTRAGE DE LA PAIE, PAR POPULATION *(file 117, 08/10/2026)*
 * ═══════════════════════════════════════════════════════════════════════════
 * 🎯 POURQUOI CET ÉCRAN EXISTE. Le lot B-ter a posé six choix qui commandent la
 *    paie — population, mode de paie, convention, portée des rubriques, régime
 *    de congés, régime maladie — et le moteur REFUSE tant qu'ils ne sont pas
 *    faits. Sans écran, ils ne pouvaient être posés que par migration ou appel
 *    direct. ***Un refus qui dit quoi faire, sans porte pour le faire, déplace
 *    le mur — il ne l'ouvre pas.***
 *
 * ⛔ LE GARDE-FOU QUI COMMANDE TOUT ICI. GSS-360 sert la sécurité privée de
 *    toute la zone OHADA. **Aucun tenant n'est un modèle, aucun pays non plus.**
 *    Cet écran n'écrit AUCUNE liste de valeurs : modes, régimes, conventions et
 *    régimes de congés viennent tous de `GET /populations/referentiel-pays`.
 *    *Une liste recopiée ici cesserait de suivre le produit le jour où le
 *    produit change, et personne ne le verrait.*
 *
 * ⚠️ QUI ÉCRIT — ARBITRAGE DE DOM. `dg` et `admin` lisent ET écrivent ; `rh`
 *    LIT SEULEMENT. Les boutons d'écriture sont ABSENTS pour `rh`, pas grisés :
 *    **règle 26**, le produit n'explique pas ses restrictions. Un bouton grisé
 *    n'apparaît ici que pour une DONNÉE qui manque — et il dit laquelle.
 *
 * ⛔ AUCUNE ALERTE NATIVE DU NAVIGATEUR. Ni `alert`, ni `confirm`, ni `prompt` :
 *    la désactivation d'une population se confirme DANS la page.
 * ⛔ UNE VALEUR ABSENTE S'AFFICHE « à renseigner », JAMAIS `0` NI `-`.
 */
import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import { useAuth } from 'context/AuthContext';
import useEscapeClose from 'hooks/useEscapeClose';
import { aujourdHui } from '../../../../utils/jour';

/* ⓘ « à renseigner » et non `0` ni `-` : un zéro se lit comme une valeur, un
 *   tiret comme une absence définitive. Ici c'est un acte qui reste à faire. */
const A_RENSEIGNER = 'à renseigner';

/* ⚠️ CES LIBELLÉS NE SONT PAS UNE LISTE DE VALEURS — c'est la traduction en
 *    français des codes que LE SERVEUR envoie. Un code inconnu de cette table
 *    s'affiche tel quel : *inventer un libellé pour un code qu'on ne connaît
 *    pas masquerait l'arrivée d'une valeur nouvelle.* */
const LIBELLES = {
  pointage_journalier: 'Au pointage journalier',
  mensuel_par_exception: 'Mensuel, par exception',
  non_payee: 'Non payée',
  payee_avec_justificatif: 'Payée avec justificatif',
  payee_sans_condition: 'Payée sans condition',
  plein_salaire: 'Plein salaire',
  bareme_convention: 'Barème de la convention',
  maintien: 'Maintien du salaire',
  indemnite: 'Indemnité',
  allocation: 'Allocation d’un organisme',
  mode_paie: 'mode de paie',
  convention: 'convention collective',
};
const dire = (code) => (code ? (LIBELLES[code] || code) : A_RENSEIGNER);

const aujourdhui = () => aujourdHui();

/* ── Le bandeau d'un acte : ce qui s'est passé, ou ce qui a été refusé ────── */
function Bandeau({ etat }) {
  if (!etat) return null;
  const rouge = etat.type === 'refus';
  return (
    <div style={{
      border: `1px solid ${rouge ? '#b71c1c' : '#2e7d32'}`,
      background: rouge ? '#fdecea' : '#edf7ed',
      color: rouge ? '#8e1c14' : '#1b5e20',
      borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: 13, marginBottom: '1rem',
    }}>
      {etat.texte}
    </div>
  );
}

/* ── La mention informative du choix moins favorable ──────────────────────────
 * ⛔ ELLE N'EST PAS UN BLOCAGE ET NE PART SUR AUCUN BULLETIN. Le choix du tenant
 *    s'applique tel quel. *Bloquer un choix moins favorable serait arbitrer un
 *    litige du travail depuis un logiciel de paie ; l'imprimer sur la pièce du
 *    salarié en ferait une pièce à charge.* Elle se dit à qui paramètre.
 * ⚠️ LE TEXTE VIENT DU SERVEUR, mot pour mot. Le réécrire ici en ferait une
 *    seconde vérité.
 */
function Mention({ mention }) {
  if (!mention) return null;
  return (
    <div style={{
      border: '1px solid #B45309', background: '#fff8e1', color: '#7c4a03',
      borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: 13, marginTop: '0.75rem',
    }}>
      <strong>Mention informative </strong>— {mention.message}
    </div>
  );
}

/* ── Un bloc de choix, avec sa date d'effet et son motif ──────────────────────
 * ⚠️ DATE D'EFFET ET MOTIF SUR CHAQUE BLOC, parce que le serveur les exige sur
 *    chaque route : un choix de paie s'applique à partir d'une date, et le motif
 *    dit pourquoi il a été fait. *Un écran qui les omettrait collectionnerait
 *    des 400 que l'utilisateur lirait comme des pannes.*
 */
/* ═══════════════════════════════════════════════════════════════════════════
 * L'HISTORIQUE D'UN CHOIX, ET LES DEUX GESTES QUI LE BORNENT  *(file 121)*
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ CLÔTURER ET ANNULER NE SE CONFONDENT PAS, ET L'ÉCRAN DOIT LE DIRE.
 *    · Clôturer : le choix a été VRAI, il cesse de s'appliquer à une date. La
 *      ligne reste et borne sa période.
 *    · Annuler : le choix n'aurait jamais dû être saisi. La ligne sort de la
 *      résolution, **sans jamais disparaître**.
 *    *Les présenter comme deux variantes du même bouton ferait annuler des
 *    choix qui ont été vrais — et l'inverse.*
 * ⓘ LES DEUX RÈGLES VIVENT DANS LA BASE, pas ici. L'écran n'empêche rien et ne
 *   recopie aucune borne : il envoie le geste et AFFICHE le refus, qui nomme
 *   déjà la période émise concernée. *Un écran qui recopie une règle finit par
 *   en appliquer une autre que celle du moteur.*
 * ⓘ Il dit tout de même la borne quand il la connaît — non pour décider, mais
 *   pour éviter à l'utilisateur un refus qu'il pouvait prévoir.
 * ═══════════════════════════════════════════════════════════════════════════ */
function HistoriqueChoix({ lignes, charge, borne, periodeEmise, peutEcrire, enVol, onCloturer, onAnnuler }) {
  const [geste, setGeste] = useState(null); // { type: 'cloture'|'annulation', id }
  const [dateFin, setDateFin] = useState(borne || aujourdhui());
  const [motif, setMotif] = useState('');

  if (!lignes || lignes.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 8 }}>
        Aucune ligne enregistrée pour ce choix.
      </div>
    );
  }

  const envoyer = async () => {
    const ok = geste.type === 'cloture'
      ? await onCloturer({ ligne_id: geste.id, date_fin: dateFin, motif })
      : await onAnnuler({ ligne_id: geste.id, motif });
    if (ok) { setGeste(null); setMotif(''); }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gss-text-muted, #777)', marginBottom: 6 }}>
        Historique — {lignes.length} ligne{lignes.length > 1 ? 's' : ''}
      </div>
      {lignes.map((l) => (
        <div key={l.id} style={{
          fontSize: 12, padding: '6px 8px', borderRadius: 4, marginBottom: 4,
          background: l.en_vigueur ? 'rgba(52,208,88,0.10)' : 'var(--gss-bg-soft, #fafafa)',
          opacity: l.annule_le ? 0.6 : 1,
          border: l.couvre_mois_en_cours && !l.en_vigueur ? '1px solid #B45309' : '1px solid transparent',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
            <div>
              <strong>{charge.map((c) => dire(l[c])).filter(Boolean).join(' — ')}</strong>
              {' · du '}{l.date_effet}
              {l.date_fin ? ` au ${l.date_fin}` : ' (ouvert)'}
              {l.en_vigueur && <span style={{ color: '#2E7D32', fontWeight: 600 }}> · en vigueur</span>}
              {/* ⚠️ SIGNALÉ, PARCE QUE C'EST LE CAS DANGEREUX : une ligne qui
                  couvre le mois en cours sans être celle en vigueur veut dire
                  que le mois est coupé en deux. */}
              {l.couvre_mois_en_cours && !l.en_vigueur
                && <span style={{ color: '#B45309', fontWeight: 600 }}> · couvre le mois en cours</span>}
              {l.annule_le && <span style={{ color: '#B45309' }}> · annulée</span>}
            </div>
            {peutEcrire && !l.annule_le && (
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {!l.date_fin && (
                  <button className="gss-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                    onClick={() => { setGeste({ type: 'cloture', id: l.id }); setDateFin(borne || aujourdhui()); }}>
                    Clôturer
                  </button>
                )}
                <button className="gss-btn" style={{ fontSize: 11, padding: '2px 8px' }}
                  onClick={() => setGeste({ type: 'annulation', id: l.id })}>
                  Annuler
                </button>
              </div>
            )}
          </div>
          <div style={{ color: 'var(--gss-text-muted, #777)', marginTop: 2 }}>
            {l.motif ? `« ${l.motif} »` : 'sans motif'}
            {l.acteur_nom ? ` — ${l.acteur_nom}` : ''}
            {l.created_at ? `, saisi le ${String(l.created_at).slice(0, 10)}` : ''}
            {l.annule_le && (
              <> · annulée le {String(l.annule_le).slice(0, 10)}
                {l.annule_par_nom ? ` par ${l.annule_par_nom}` : ''}
                {l.motif_annulation ? ` — « ${l.motif_annulation} »` : ''}
              </>
            )}
          </div>

          {geste && geste.id === l.id && (
            <div style={{ marginTop: 8, padding: 8, background: 'var(--gss-bg, #fff)', borderRadius: 4 }}>
              {geste.type === 'cloture' ? (
                <>
                  <div style={{ marginBottom: 6 }}>
                    <strong>Clôturer</strong> — ce choix a été vrai, il cesse de s’appliquer.
                  </div>
                  {/* ⓘ La borne est DITE, pas imposée : c'est la base qui refuse. */}
                  {borne && (
                    <div style={{ color: 'var(--gss-text-muted, #777)', marginBottom: 6 }}>
                      La paie de {periodeEmise} est émise : la première date de fin possible
                      est le {borne}.
                    </div>
                  )}
                  <div className="gss-field">
                    <label>Date de fin *</label>
                    <input className="gss-input" type="date" value={dateFin}
                      onChange={(e) => setDateFin(e.target.value)} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: 6 }}>
                    <strong>Annuler</strong> — cette ligne a été saisie par erreur. Elle sortira
                    de la résolution et <em>restera enregistrée</em>, avec votre motif.
                  </div>
                  <div style={{ color: 'var(--gss-text-muted, #777)', marginBottom: 6 }}>
                    Impossible si une paie déjà émise couvre sa période.
                  </div>
                </>
              )}
              <div className="gss-field">
                <label>Motif *</label>
                <input className="gss-input" value={motif} placeholder="Pourquoi ce geste"
                  onChange={(e) => setMotif(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button className="gss-btn gss-btn-primary" disabled={enVol || !motif.trim()}
                  onClick={envoyer}>Confirmer</button>
                <button className="gss-btn" onClick={() => { setGeste(null); setMotif(''); }}>
                  Abandonner
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BlocChoix({
  titre, aide, enCours, children, onPoser, peutEcrire, empeche, enVol,
  historique, charge, borne, periodeEmise, onCloturer, onAnnuler,
}) {
  const [ouvert, setOuvert] = useState(false);
  const [dateEffet, setDateEffet] = useState(aujourdhui());
  const [motif, setMotif] = useState('');

  const poser = async () => {
    const ok = await onPoser({ date_effet: dateEffet, motif });
    if (ok) { setOuvert(false); setMotif(''); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--gss-border, #e5e5e5)', padding: '0.9rem 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{titre}</div>
          <div style={{ fontSize: 13, color: enCours ? 'inherit' : '#B45309', marginTop: 2 }}>
            {enCours || A_RENSEIGNER}
          </div>
          {aide && <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 4 }}>{aide}</div>}
        </div>
        {/* ⛔ ABSENT POUR `rh`, PAS GRISÉ — règle 26. */}
        {peutEcrire && (
          <button
            className="gss-btn"
            disabled={!!empeche}
            /* ⚠️ UN BOUTON GRISÉ DIT POURQUOI. Ici le gris ne vient JAMAIS d'un
             *    rôle — il vient d'une donnée qui manque, et `title` la nomme. */
            title={empeche || ''}
            onClick={() => setOuvert((v) => !v)}
          >
            {ouvert ? 'Fermer' : 'Modifier'}
          </button>
        )}
      </div>
      {empeche && peutEcrire && (
        <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>{empeche}</div>
      )}
      {ouvert && !empeche && (
        <div style={{ marginTop: '0.75rem', background: 'var(--gss-bg-soft, #fafafa)', padding: '0.9rem', borderRadius: 6 }}>
          {children}
          <div className="gss-form-grid" style={{ marginTop: '0.75rem' }}>
            <div className="gss-field">
              <label>Date d’effet *</label>
              <input className="gss-input" type="date" value={dateEffet}
                onChange={(e) => setDateEffet(e.target.value)} />
            </div>
            <div className="gss-field">
              <label>Motif *</label>
              <input className="gss-input" value={motif} placeholder="Pourquoi ce choix"
                onChange={(e) => setMotif(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="gss-btn gss-btn-primary" disabled={!motif || !dateEffet || enVol}
              title={!motif ? 'Le motif est obligatoire : une décision se signe.' : ''}
              onClick={poser}>
              {enVol ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button className="gss-btn-cancel" onClick={() => setOuvert(false)}>Annuler</button>
          </div>
        </div>
      )}
      {/* ⓘ L'HISTORIQUE EST TOUJOURS VISIBLE, pas replié derrière « Modifier ».
        *   Le replier reviendrait à cacher ce que l'écran avait justement pour
        *   défaut de taire — et c'est en le voyant que Dom a trouvé les deux
        *   lignes de mode de paie ouvertes. */}
      {historique && (
        <HistoriqueChoix
          lignes={historique} charge={charge || []}
          borne={borne} periodeEmise={periodeEmise}
          peutEcrire={peutEcrire} enVol={enVol}
          onCloturer={onCloturer} onAnnuler={onAnnuler}
        />
      )}
    </div>
  );
}

export default function ParametragePaiePage() {
  const { user } = useAuth();
  const roles = user?.roles || (user?.role ? [user.role] : []);
  /* ⛔ L'ÉCRAN SUIT LE SERVEUR, JAMAIS L'INVERSE : cette liste est la copie
   *    EXACTE de `canWrite` dans `populationRoutes.js` (`admin, dg`). Si elle
   *    s'élargissait ici, l'écran offrirait des actes que la route refuse. */
  const peutEcrire = roles.some((r) => r === 'dg' || r === 'admin');

  const [onglet, setOnglet] = useState('populations');
  const [populations, setPopulations] = useState(null);
  const [referentiel, setReferentiel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [histo, setHisto] = useState(null);
  const [choisie, setChoisie] = useState('');
  const [etat, setEtat] = useState(null);
  const [enVol, setEnVol] = useState(false);
  const [creation, setCreation] = useState(false);

  useEscapeClose(creation, () => setCreation(false));

  const refus = (err, defaut) => setEtat({
    type: 'refus',
    texte: err?.response?.data?.error || defaut,
  });

  const charger = useCallback(async () => {
    try {
      const [pop, ref] = await Promise.all([
        api.get('/populations'),
        api.get('/populations/referentiel-pays'),
      ]);
      setPopulations(pop.data.populations || []);
      setReferentiel(ref.data);
    } catch (err) { refus(err, 'Le paramétrage n’a pas pu être lu.'); }
  }, []);

  /* ⚠️ DEUX LECTURES, ET ELLES NE DISENT PAS LA MÊME CHOSE. `GET /:id` rend la
   *    RÉSOLUTION — la ligne en vigueur aujourd'hui. `GET /:id/historique` rend
   *    TOUTES les lignes des quatre choix, closes et annulées comprises, plus
   *    la borne de clôture. *Afficher la seule résolution laissait invisibles
   *    les lignes antérieures ET postérieures : en production, une population
   *    portait deux modes de paie ouverts et l'écran n'en montrait qu'un.* */
  const chargerDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); setHisto(null); return; }
    try {
      const r = await api.get(`/populations/${id}`);
      setDetail(r.data);
    } catch (err) { setDetail(null); refus(err, 'Cette population n’a pas pu être lue.'); }
    /* ⓘ L'historique est chargé SÉPARÉMENT et son échec n'empêche pas l'écran :
     *   il enrichit, il ne commande pas. */
    try {
      const h = await api.get(`/populations/${id}/historique`);
      setHisto(h.data);
    } catch { setHisto(null); }
  }, []);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { chargerDetail(choisie); }, [choisie, chargerDetail]);

  /* Un acte : il parle, il recharge, et il ne masque jamais un refus. */
  const agir = async (appel, succes) => {
    setEnVol(true); setEtat(null);
    try {
      const r = await appel();
      setEtat({ type: 'ok', texte: r?.data?.message || succes });
      await charger();
      if (choisie) await chargerDetail(choisie);
      return true;
    } catch (err) { refus(err, 'L’enregistrement a été refusé.'); return false; }
    finally { setEnVol(false); }
  };

  return (
    <div>
      <div className="content-card" style={{ marginBottom: '1rem' }}>
        <div className="content-card-title" style={{ margin: 0, border: 'none', padding: 0 }}>
          Paramétrage de la paie
        </div>
        <div style={{ fontSize: 13, color: 'var(--gss-text-muted, #777)', marginTop: 6 }}>
          Chaque population porte son mode de paie, sa convention, son régime
          maladie et, si vous le voulez, votre propre régime de congés. Rien
          n’est choisi par défaut : tant qu’un choix manque, la paie refuse en le
          nommant.
          {referentiel && ` Référentiel lu : ${referentiel.pays_code}.`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[['populations', 'Populations'], ['rattachement', 'Rattachement des salariés']].map(([k, l]) => (
          <button key={k} className={`gss-btn ${onglet === k ? 'gss-btn-primary' : ''}`}
            onClick={() => setOnglet(k)}>{l}</button>
        ))}
      </div>

      <Bandeau etat={etat} />

      {onglet === 'populations' ? (
        <VuePopulations
          populations={populations} referentiel={referentiel} detail={detail} histo={histo}
          choisie={choisie} setChoisie={setChoisie} peutEcrire={peutEcrire}
          enVol={enVol} agir={agir} creation={creation} setCreation={setCreation}
        />
      ) : (
        <VueRattachement
          populations={populations} peutEcrire={peutEcrire} enVol={enVol}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ONGLET ① — LES POPULATIONS, ET CE QUI LEUR MANQUE
 * ═══════════════════════════════════════════════════════════════════════════ */
function VuePopulations({
  populations, referentiel, detail, histo, choisie, setChoisie,
  peutEcrire, enVol, agir, creation, setCreation,
}) {
  const [neuve, setNeuve] = useState({ code: '', libelle: '', motif: '' });
  const [aDesactiver, setADesactiver] = useState(null);
  const [motifDesactivation, setMotifDesactivation] = useState('');

  if (populations === null) {
    return <div className="content-card"><p style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</p></div>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: '1rem', alignItems: 'start' }}>
      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ fontWeight: 600 }}>Vos populations</div>
          {peutEcrire && (
            <button className="gss-btn gss-btn-primary" onClick={() => setCreation(true)}>
              + Déclarer
            </button>
          )}
        </div>

        {/* ── CE QUI MANQUE SE DIT, ET NE SE DEVINE PAS ──────────────────── */}
        {populations.length === 0 && (
          <div style={{ border: '2px solid #b71c1c', borderRadius: 6, padding: '0.9rem', fontSize: 13 }}>
            <div style={{ fontWeight: 700, color: '#b71c1c', marginBottom: 4 }}>
              Aucune population n’est déclarée
            </div>
            Les populations se déclarent librement, en nombre libre, et chaque
            salarié s’y rattache. Aucune n’est proposée : c’est vous qui nommez
            les vôtres. Tant qu’aucune n’existe, aucun bulletin ne peut sortir.
          </div>
        )}

        {populations.map((p) => {
          const incomplete = p.manquants.length > 0;
          return (
            <button key={p.id} onClick={() => setChoisie(p.id)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                border: choisie === p.id ? '2px solid #1565c0' : '1px solid var(--gss-border, #e5e5e5)',
                background: choisie === p.id ? '#f0f6fd' : '#fff',
                borderRadius: 6, padding: '0.7rem 0.8rem', marginBottom: '0.5rem',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <strong style={{ fontSize: 14 }}>{p.libelle}</strong>
                {!p.actif && <span className="gss-badge" style={{ background: '#eee', color: '#666' }}>désactivée</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 2 }}>
                {p.code} · {p.effectif} salarié{p.effectif === 1 ? '' : 's'}
              </div>
              {/* ⚠️ LA POPULATION INCOMPLÈTE EST NOMMÉE, AVEC CE QUI MANQUE —
                  c'est exactement ce que le contrôle pré-paie annoncera, dans
                  les mêmes mots. */}
              <div style={{ fontSize: 12, marginTop: 4, color: incomplete ? '#b71c1c' : '#2e7d32', fontWeight: 600 }}>
                {incomplete
                  ? `incomplète — ${p.manquants.map(dire).join(', ')}`
                  : 'complète'}
              </div>
            </button>
          );
        })}
      </div>

      <div className="content-card">
        {!detail ? (
          <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--gss-text-muted, #777)' }}>
            {populations.length === 0
              ? 'Déclarez une première population pour la paramétrer.'
              : 'Choisissez une population pour voir et modifier son paramétrage.'}
          </p>
        ) : (
          <DetailPopulation
            detail={detail} referentiel={referentiel} peutEcrire={peutEcrire}
            enVol={enVol} agir={agir} histo={histo}
            onDesactiver={() => { setADesactiver(detail.population); setMotifDesactivation(''); }}
          />
        )}
      </div>

      {/* ── DÉCLARER UNE POPULATION ─────────────────────────────────────────
          ⛔ AUCUNE LISTE À COCHER, AUCUN EXEMPLE PRÉ-REMPLI. Le tenant écrit
             SES populations. *Proposer « agents / administratifs » imposerait
             une organisation d'entreprise depuis un formulaire.* */}
      {creation && (
        <div className="gss-modal-overlay" onClick={() => setCreation(false)}>
          <div className="gss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Déclarer une population</h2>
              <div className="gss-modal-subtitle">
                Elle décidera du mode de paie, de la convention et des rubriques
                de tous ceux qui s’y rattacheront.
              </div>
              <button className="gss-modal-close" onClick={() => setCreation(false)}>&times;</button>
            </div>
            <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
              <div className="gss-field">
                <label>Code *</label>
                <input className="gss-input" value={neuve.code}
                  onChange={(e) => setNeuve((n) => ({ ...n, code: e.target.value }))} />
                <small style={{ color: 'var(--gss-text-muted, #777)' }}>
                  Minuscules, chiffres et tirets bas, de 2 à 40 caractères. Il est
                  stable et sert de référence sur les bulletins.
                </small>
              </div>
              <div className="gss-field">
                <label>Libellé *</label>
                <input className="gss-input" value={neuve.libelle}
                  onChange={(e) => setNeuve((n) => ({ ...n, libelle: e.target.value }))} />
                <small style={{ color: 'var(--gss-text-muted, #777)' }}>
                  Ce que les écrans afficheront. Il se renomme librement.
                </small>
              </div>
              <div className="gss-field" style={{ gridColumn: '1 / -1' }}>
                <label>Motif *</label>
                <input className="gss-input" value={neuve.motif}
                  onChange={(e) => setNeuve((n) => ({ ...n, motif: e.target.value }))} />
                <small style={{ color: 'var(--gss-text-muted, #777)' }}>
                  Une décision d’organisation se signe : sans motif, elle ne se
                  relit pas.
                </small>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setCreation(false)}>Annuler</button>
              <button className="gss-btn-deploy"
                disabled={!neuve.code || !neuve.libelle || !neuve.motif || enVol}
                title={!neuve.motif ? 'Le motif est obligatoire.' : ''}
                onClick={async () => {
                  const ok = await agir(() => api.post('/populations', neuve),
                    `« ${neuve.code} » est déclarée.`);
                  if (ok) { setNeuve({ code: '', libelle: '', motif: '' }); setCreation(false); }
                }}>
                {enVol ? 'Enregistrement…' : 'Déclarer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DÉSACTIVER — LA CONFIRMATION VIT DANS LA PAGE ───────────────────
          ⛔ PAS DE `window.confirm` : une alerte native bloque la page et sort
             de la charte. Et elle dit ce qu'elle fait VRAIMENT — désactiver ne
             dérattache personne. */}
      {aDesactiver && (
        <div className="gss-modal-overlay" onClick={() => setADesactiver(null)}>
          <div className="gss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Désactiver « {aDesactiver.libelle} » ?</h2>
              <button className="gss-modal-close" onClick={() => setADesactiver(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem', fontSize: 13 }}>
              Elle ne s’offrira plus au rattachement. Les salariés qui y sont
              rattachés le restent, et continuent d’être payés selon son
              paramétrage : désactiver n’est pas supprimer, et ne dérattache
              personne.
              <div className="gss-field" style={{ marginTop: '1rem' }}>
                <label>Motif *</label>
                <input className="gss-input" value={motifDesactivation}
                  onChange={(e) => setMotifDesactivation(e.target.value)} />
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setADesactiver(null)}>Annuler</button>
              <button className="gss-btn-deploy" disabled={!motifDesactivation || enVol}
                title={!motifDesactivation ? 'Le motif est obligatoire.' : ''}
                onClick={async () => {
                  const ok = await agir(
                    () => api.patch(`/populations/${aDesactiver.id}`,
                      { actif: false, motif: motifDesactivation }),
                    `« ${aDesactiver.code} » est désactivée.`);
                  if (ok) setADesactiver(null);
                }}>
                {enVol ? 'Enregistrement…' : 'Désactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LE DÉTAIL D'UNE POPULATION — SIX BLOCS, SIX CHOIX
 * ═══════════════════════════════════════════════════════════════════════════ */
/* ⚠️ `histo` EST UNE PROP, PAS UN APPEL FAIT ICI. Les quatre blocs partagent un
 *    seul chargement — un par bloc ferait quatre allers-retours pour une même
 *    réponse, et quatre états à garder d'accord. */
function DetailPopulation({ detail, referentiel, peutEcrire, enVol, agir, histo, onDesactiver }) {
  const p = detail.population;
  const ref = referentiel || {};

  /* ⓘ LES PROPS D'HISTORIQUE, FABRIQUÉES UNE FOIS POUR LES QUATRE BLOCS. Les
   *   écrire quatre fois serait quatre endroits à corriger le jour où la route
   *   change de forme — et un seul qu'on penserait à ouvrir.
   * ⚠️ `charge` dit QUELLES COLONNES porter à l'écran pour ce choix : c'est la
   *    même liste que `CHOIX` dans `populationRoutes.js`. Si les deux
   *    divergeaient, l'historique afficherait une ligne vide. */
  const CHARGE = {
    'mode-paie': ['mode'],
    convention: ['convention_code'],
    'regime-conges': ['regime'],
    'regime-maladie': ['regime', 'mode_paiement'],
  };
  const propsHisto = (cle) => ({
    charge: CHARGE[cle],
    historique: histo && histo.choix ? histo.choix[cle] : null,
    borne: histo ? histo.borne_cloture : null,
    periodeEmise: histo ? histo.derniere_periode_emise : null,
    onCloturer: ({ ligne_id: id, date_fin: f, motif: m }) => agir(
      () => api.post(`/populations/${p.id}/${cle}/cloture`,
        { ligne_id: id, date_fin: f, motif: m }),
      'Le choix est clôturé.'),
    onAnnuler: ({ ligne_id: id, motif: m }) => agir(
      () => api.post(`/populations/${p.id}/${cle}/annulation`,
        { ligne_id: id, motif: m }),
      'La ligne est annulée — elle reste enregistrée.'),
  });
  const [mode, setMode] = useState('');
  const [convention, setConvention] = useState('');
  const [regimeConges, setRegimeConges] = useState('');
  const [regimeMaladie, setRegimeMaladie] = useState('');
  const [modeMaladie, setModeMaladie] = useState('');
  const [libelle, setLibelle] = useState(p.libelle);
  const [motifRenommage, setMotifRenommage] = useState('');
  const [renommer, setRenommer] = useState(false);

  useEffect(() => {
    setLibelle(p.libelle); setRenommer(false); setMotifRenommage('');
    setMode(''); setConvention(''); setRegimeConges('');
    setRegimeMaladie(''); setModeMaladie('');
  }, [p.id, p.libelle]);

  const conventions = ref.conventions || [];

  return (
    <div>
      <div className="gss-toolbar">
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{p.libelle}</div>
          <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)' }}>
            {p.code} · {p.effectif} salarié{p.effectif === 1 ? '' : 's'}
            {!p.actif && ' · désactivée'}
          </div>
        </div>
        {peutEcrire && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="gss-btn" onClick={() => setRenommer((v) => !v)}>Renommer</button>
            {p.actif && <button className="gss-btn" onClick={onDesactiver}>Désactiver</button>}
          </div>
        )}
      </div>

      {renommer && peutEcrire && (
        <div style={{ background: 'var(--gss-bg-soft, #fafafa)', padding: '0.9rem', borderRadius: 6, marginBottom: '0.5rem' }}>
          <div className="gss-form-grid">
            <div className="gss-field">
              <label>Libellé *</label>
              <input className="gss-input" value={libelle} onChange={(e) => setLibelle(e.target.value)} />
            </div>
            <div className="gss-field">
              <label>Motif *</label>
              <input className="gss-input" value={motifRenommage}
                onChange={(e) => setMotifRenommage(e.target.value)} />
            </div>
          </div>
          {/* ⓘ LE CODE NE SE CHANGE PAS, et l'écran le dit au lieu d'offrir un
              champ que la route refuserait. */}
          <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 6 }}>
            Le code « {p.code} » ne change pas : il sert de référence sur les
            pièces déjà émises.
          </div>
          <button className="gss-btn gss-btn-primary" style={{ marginTop: '0.5rem' }}
            disabled={!libelle.trim() || !motifRenommage || enVol}
            title={!motifRenommage ? 'Le motif est obligatoire.' : ''}
            onClick={async () => {
              const ok = await agir(() => api.patch(`/populations/${p.id}`,
                { libelle, motif: motifRenommage }), 'Le libellé est à jour.');
              if (ok) setRenommer(false);
            }}>
            {enVol ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}

      {/* ── ① MODE DE PAIE ─────────────────────────────────────────────────── */}
      <BlocChoix
        titre="Mode de paie"
        enCours={detail.mode_paie
          ? `${dire(detail.mode_paie.mode)} — depuis le ${detail.mode_paie.date_effet}`
          : null}
        aide="Au pointage journalier, les jours payés viennent du pointage. Mensuel par exception, le mois est dû et les exceptions l’ajustent."
        peutEcrire={peutEcrire} enVol={enVol}
        {...propsHisto('mode-paie')}
        onPoser={({ date_effet: de, motif }) => {
          if (!mode) return false;
          return agir(() => api.post(`/populations/${p.id}/mode-paie`,
            { mode, date_effet: de, motif }), 'Le mode de paie est posé.');
        }}
      >
        {/* ⛔ AUCUN PRÉ-SÉLECTIONNÉ. La liste vient du serveur. */}
        <select className="gss-input" value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="">— Choisir le mode —</option>
          {(ref.modes_paie_possibles || []).map((m) => (
            <option key={m} value={m}>{dire(m)}</option>
          ))}
        </select>
      </BlocChoix>

      {/* ── ② CONVENTION ───────────────────────────────────────────────────── */}
      <BlocChoix
        titre="Convention collective"
        enCours={detail.convention
          ? `${detail.convention.convention_code} — depuis le ${detail.convention.date_effet}`
          : null}
        aide="Elle est figée sur chaque bulletin au moment de l’émission."
        peutEcrire={peutEcrire} enVol={enVol}
        {...propsHisto('convention')}
        /* ⚠️ GRISÉ PARCE QU'UNE DONNÉE MANQUE, ET IL DIT LAQUELLE. Un pays sans
         *    convention au référentiel n'est pas un pays sans convention : le
         *    tenant n'a simplement rien à rattacher, et la paie ne le lui
         *    reproche pas (refus conditionnel de la 092). */
        empeche={conventions.length === 0
          ? `Le référentiel de ${ref.pays_code || 'ce pays'} ne déclare aucune convention à ce jour : il n’y a rien à rattacher, et la paie ne l’exige pas.`
          : null}
        onPoser={({ date_effet: de, motif }) => {
          if (!convention) return false;
          return agir(() => api.post(`/populations/${p.id}/convention`,
            { convention_code: convention, date_effet: de, motif }),
          'La convention est rattachée.');
        }}
      >
        <select className="gss-input" value={convention} onChange={(e) => setConvention(e.target.value)}>
          <option value="">— Choisir la convention —</option>
          {conventions.map((cv) => (
            <option key={cv.code} value={cv.code}>{cv.code} — {cv.libelle || cv.code}</option>
          ))}
        </select>
      </BlocChoix>

      {/* ── ③ RÉGIME DE CONGÉS ─────────────────────────────────────────────── */}
      <BlocChoix
        titre="Régime de congés"
        /* ⚠️ « depuis le … » AJOUTÉ ICI (file 121 ②). Ce bloc et le suivant
         *    étaient les DEUX SEULS à ne dire aucune date, alors que la route la
         *    rend — et c'est le choix qui commande ce qu'on paie pendant un
         *    arrêt de travail. *Un choix daté dont l'écran tait la date laisse
         *    croire qu'il a toujours été là.*
         *    ⓘ La date n'est dite que pour un choix DU TENANT : la référence du
         *      pays n'a pas de date d'effet propre à cette population. */
        enCours={detail.regime_conges && detail.regime_conges.regime
          ? `${dire(detail.regime_conges.regime)} — ${detail.regime_conges.origine === 'tenant'
            ? `votre choix${detail.regime_conges.date_effet
              ? `, depuis le ${detail.regime_conges.date_effet}` : ''}`
            : `référence de ${detail.regime_conges.pays_code}`}`
          : null}
        aide="Sans choix de votre part, la référence du pays s’applique. Votre choix, s’il existe, la remplace."
        peutEcrire={peutEcrire} enVol={enVol}
        {...propsHisto('regime-conges')}
        onPoser={({ date_effet: de, motif }) => {
          if (!regimeConges) return false;
          return agir(() => api.post(`/populations/${p.id}/regime-conges`,
            { regime: regimeConges, date_effet: de, motif }), 'Le régime de congés est posé.');
        }}
      >
        <select className="gss-input" value={regimeConges} onChange={(e) => setRegimeConges(e.target.value)}>
          <option value="">— Choisir le régime —</option>
          {(ref.regimes_conges_possibles || []).map((x) => (
            <option key={x} value={x}>{dire(x)}</option>
          ))}
        </select>
        <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 6 }}>
          Référence de {ref.pays_code} : {ref.regime_conges_reference
            ? dire(ref.regime_conges_reference)
            : 'aucune au référentiel'}.
        </div>
      </BlocChoix>
      <Mention mention={detail.mention_conges} />

      {/* ── ④ RÉGIME MALADIE ───────────────────────────────────────────────── */}
      <BlocChoix
        titre="Régime maladie"
        /* ⚠️ « depuis le … » AJOUTÉ ICI AUSSI — même raison qu'au bloc
         *    précédent, et elle vaut doublement : ce choix décide de ce qui est
         *    payé pendant un arrêt de travail. */
        enCours={detail.regime_maladie
          ? `${dire(detail.regime_maladie.regime)}${detail.regime_maladie.mode_paiement
            ? ` — ${dire(detail.regime_maladie.mode_paiement)}` : ''}`
            + `${detail.regime_maladie.date_effet
              ? ` — depuis le ${detail.regime_maladie.date_effet}` : ''}`
          : null}
        /* ⓘ DIT QUE LE REFUS EST CONDITIONNEL, parce qu'il l'est : une
            population qui n'a jamais de jours Maladie n'est jamais refusée. */
        aide="Le choix n’est exigé que si un mois porte des jours de maladie. Trois régimes, et aucun n’est appliqué par défaut."
        peutEcrire={peutEcrire} enVol={enVol}
        {...propsHisto('regime-maladie')}
        onPoser={({ date_effet: de, motif }) => {
          if (!regimeMaladie) return false;
          const corps = { regime: regimeMaladie, date_effet: de, motif };
          /* ⚠️ LE MODE NE PART QUE SOUS « payée avec justificatif ». La route le
           *    refuse ailleurs, et le `CHECK` de la 093 l'impose en base. */
          if (regimeMaladie === 'payee_avec_justificatif') corps.mode_paiement = modeMaladie;
          return agir(() => api.post(`/populations/${p.id}/regime-maladie`, corps),
            'Le régime maladie est posé.');
        }}
      >
        {/* ⓘ LA RÉFÉRENCE DU PAYS, AU-DESSUS DU CHOIX — comme au bloc des congés
            (file 119). Le tenant doit voir ce que son pays déclare avant de
            décider s'il s'en écarte.
            ⚠️ ELLE N'EST PAS DANS LA LISTE DÉROULANTE, et ce n'est pas un oubli :
            le pays déclare un RÉGIME (« maintien »…), le tenant choisit entre
            trois régimes d'un AUTRE vocabulaire. *Les mélanger ferait croire que
            le pays a déjà choisi à la place du tenant.* */}
        <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginBottom: 6 }}>
          Référence de {ref.pays_code} : {ref.regime_maladie_reference
            ? dire(ref.regime_maladie_reference)
            : 'aucune au référentiel'}
          {ref.bareme_maladie_declare
            ? ` · barème de la convention : ${ref.bareme_maladie_tranches} tranche(s) d’ancienneté`
            : ' · aucun barème au référentiel'}
        </div>
        <select className="gss-input" value={regimeMaladie}
          onChange={(e) => { setRegimeMaladie(e.target.value); setModeMaladie(''); }}>
          <option value="">— Choisir le régime —</option>
          {(ref.regimes_maladie_possibles || []).map((x) => (
            <option key={x} value={x}>{dire(x)}</option>
          ))}
        </select>
        {/* ⛔ LE MODE DE PAIEMENT N'EXISTE À L'ÉCRAN QUE SOUS LE RÉGIME ②.
            Sous les deux autres, il n'y a pas de mode à choisir, et un champ
            affiché puis ignoré se lirait comme un choix pris en compte. */}
        {regimeMaladie === 'payee_avec_justificatif' && (
          <div style={{ marginTop: '0.5rem' }}>
            <select className="gss-input" value={modeMaladie}
              onChange={(e) => setModeMaladie(e.target.value)}>
              <option value="">— Choisir le mode de paiement —</option>
              {(ref.modes_paiement_maladie_possibles || []).map((x) => (
                <option key={x} value={x}>{dire(x)}</option>
              ))}
            </select>
            {/* ⚠️ ON LE DIT AVANT LE CHOIX, PAS APRÈS LE REFUS. Choisir le
                barème d'un pays qui n'en déclare pas est permis — la paie
                refusera alors en `bareme_maladie_non_renseigne`, et le savoir
                d'avance est tout ce que l'écran peut offrir. */}
            {modeMaladie === 'bareme_convention' && !ref.bareme_maladie_declare && (
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>
                Le référentiel de {ref.pays_code} ne déclare aucun barème de
                maladie à ce jour. Ce choix reste le vôtre, mais la paie refusera
                tant que le barème n’est pas renseigné.
              </div>
            )}
          </div>
        )}
      </BlocChoix>

      {/* ── ⑤ PORTÉE DES RUBRIQUES ─────────────────────────────────────────── */}
      <PorteeRubriques
        populationId={p.id} libelle={p.libelle}
        restreintes={detail.rubriques_restreintes || []}
        peutEcrire={peutEcrire} enVol={enVol} agir={agir}
      />

      {/* ── CE QUI MANQUE, REDIT ICI, AVEC LES MOTS DU MOTEUR ─────────────── */}
      <div style={{ borderTop: '1px solid var(--gss-border, #e5e5e5)', paddingTop: '0.9rem', marginTop: '0.5rem' }}>
        {detail.manquants.length > 0 ? (
          <div style={{ color: '#b71c1c', fontSize: 13 }}>
            <strong>« {p.libelle} » est incomplète</strong> — il manque :{' '}
            {detail.manquants.map(dire).join(', ')}. Aucun bulletin ne sortira
            pour ses salariés tant que ces choix ne sont pas faits, et le
            contrôle avant génération les nommera de la même façon.
          </div>
        ) : (
          <div style={{ color: '#2e7d32', fontSize: 13 }}>
            <strong>« {p.libelle} » est complète.</strong>
            {detail.regime_maladie_a_choisir_si_maladie && ' Le régime maladie reste à choisir si un mois porte des jours de maladie.'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── LA PORTÉE DES RUBRIQUES ──────────────────────────────────────────────────
 * ⚠️ UNE RUBRIQUE SANS PORTÉE N'EST PAS RESTREINTE — c'est l'ABSENCE de
 *    restriction, et non « destinée à personne ». *Décider le contraire
 *    arrêterait de payer, du jour au lendemain, des primes versées depuis des
 *    mois.* L'écran dit donc « toutes les populations », jamais « aucune ».
 * ⓘ La route REMPLACE la portée en entier (`PUT`) : on envoie la liste complète
 *   des populations visées, pas un ajout.
 */
function PorteeRubriques({ populationId, libelle, restreintes, peutEcrire, enVol, agir }) {
  const [rubriques, setRubriques] = useState(null);
  const [refusLecture, setRefusLecture] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await api.get('/populations/rubriques');
      setRubriques(r.data.rubriques || []);
      setRefusLecture(r.data.refus || null);
    } catch { setRubriques([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const basculer = async (rub) => {
    const vise = rub.population_ids.includes(populationId);
    const ids = vise
      ? rub.population_ids.filter((x) => x !== populationId)
      : rub.population_ids.concat([populationId]);
    const ok = await agir(() => api.put(`/populations/rubriques/${rub.code}/portee`,
      { population_ids: ids, motif }), 'La portée est à jour.');
    if (ok) await charger();
  };

  return (
    <div style={{ borderTop: '1px solid var(--gss-border, #e5e5e5)', padding: '0.9rem 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Rubriques réservées à cette population</div>
          <div style={{ fontSize: 13, marginTop: 2 }}>
            {restreintes.length > 0 ? restreintes.join(', ') : 'aucune réservation'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 4 }}>
            Une rubrique sans réservation s’applique à toutes les populations. Une
            rubrique réservée, appliquée ailleurs, fait refuser le bulletin.
          </div>
        </div>
        {peutEcrire && rubriques && rubriques.length > 0 && (
          <button className="gss-btn" onClick={() => setOuvert((v) => !v)}>
            {ouvert ? 'Fermer' : 'Modifier'}
          </button>
        )}
      </div>

      {refusLecture && (
        <div style={{ fontSize: 12, color: '#b71c1c', marginTop: 6 }}>{refusLecture.detail}</div>
      )}
      {rubriques && rubriques.length === 0 && !refusLecture && (
        <div style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', marginTop: 6 }}>
          Aucune rubrique n’est en vigueur pour cette société à ce jour : il n’y a
          pas de portée à poser.
        </div>
      )}

      {ouvert && peutEcrire && (
        <div style={{ marginTop: '0.75rem', background: 'var(--gss-bg-soft, #fafafa)', padding: '0.9rem', borderRadius: 6 }}>
          <div className="gss-field">
            <label>Motif *</label>
            <input className="gss-input" value={motif} onChange={(e) => setMotif(e.target.value)} />
            <small style={{ color: 'var(--gss-text-muted, #777)' }}>
              Restreindre une rubrique est une décision, et une décision se signe.
            </small>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            {(rubriques || []).map((rub) => {
              const vise = rub.population_ids.includes(populationId);
              return (
                <div key={rub.code} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.3rem 0' }}>
                  <button className="gss-btn" disabled={!motif || enVol}
                    title={!motif ? 'Le motif est obligatoire.' : ''}
                    onClick={() => basculer(rub)}>
                    {vise ? 'Retirer' : 'Réserver'}
                  </button>
                  <span style={{ fontSize: 13 }}>
                    <strong>{rub.code}</strong> — {rub.libelle}
                    {' · '}
                    {rub.restreinte
                      ? `réservée à ${rub.population_ids.length} population(s)${vise ? `, dont « ${libelle} »` : ''}`
                      : 'non restreinte'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ONGLET ② — RATTACHER LES SALARIÉS, UN PAR UN OU EN MASSE
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ LE RETOUR DE LA ROUTE DISTINGUE RATTACHÉS ET NON RATTACHÉS, et l'écran
 *    AFFICHE LES DEUX. Un identifiant inconnu, ou un salarié déjà sorti de
 *    l'effectif, ne lève pas d'erreur — *et ne doit pas passer pour un succès
 *    silencieux.*
 */
function VueRattachement({ populations, peutEcrire, enVol }) {
  const [agents, setAgents] = useState(null);
  const [cible, setCible] = useState('');
  const [selection, setSelection] = useState([]);
  const [sansPopulation, setSansPopulation] = useState(false);
  const [bilan, setBilan] = useState(null);
  /* ═══════════════════════════════════════════════════════════════════════
     MAQUETTE ① — UNE DATE D'EFFET ET UN MOTIF POUR TOUT LE LOT  *(C2b)*
     ═══════════════════════════════════════════════════════════════════════
     🎯 RÈGLE TRANCHÉE PAR DOM LE 13/10/2026 : « UNE date d'effet et UN motif
        pour TOUT le lot. Des dates différentes = PLUSIEURS lots. »
     ⛔ AUCUNE VALEUR PAR DÉFAUT, ET SURTOUT PAS LA DATE DU JOUR. Un
        rattachement décidé la semaine dernière et saisi aujourd'hui prendrait
        effet au mauvais moment — et le bulletin suivrait, sans que personne
        puisse dire quand le changement a vraiment eu lieu.
     ⓘ Le serveur exige les deux et les nomme séparément ; l'écran les demande
       avant de proposer le bouton, pour ne pas faire cliquer dans le vide. */
  const [dateEffet, setDateEffet] = useState('');
  const [motif, setMotif] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await api.get('/agents');
      setAgents(Array.isArray(r.data) ? r.data : (r.data?.agents || []));
    } catch { setAgents([]); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const parId = new Map((populations || []).map((p) => [p.id, p]));
  /* ⓘ Les désactivées ne s'offrent plus comme CIBLE, mais celles qui portent
   *   déjà des salariés continuent de s'afficher dans la colonne. */
  const cibles = (populations || []).filter((p) => p.actif);

  const liste = (agents || [])
    .filter((a) => !a.date_sortie)
    .filter((a) => (sansPopulation ? !a.population_id : true));

  const rattacher = async () => {
    setBilan(null);
    try {
      const r = await api.put(`/populations/${cible}/agents`, {
        agent_ids: selection, date_effet: dateEffet, motif: motif.trim(),
      });
      setBilan(r.data);
      setSelection([]);
      /* ⓘ LA DATE ET LE MOTIF NE SONT PAS VIDÉS : un second lot se fait souvent
       *   avec la même date (une réorganisation qui prend effet le même jour),
       *   et les retaper serait une invitation à se tromper. La SÉLECTION, elle,
       *   est vidée — c'est le lot qui est terminé, pas la décision. */
      await charger();
    } catch (err) {
      setBilan({ message: err?.response?.data?.error || 'Le rattachement a été refusé.', refus: true });
    }
  };

  if (agents === null) {
    return <div className="content-card"><p style={{ padding: '2rem', textAlign: 'center' }}>Chargement…</p></div>;
  }

  return (
    <div className="content-card">
      <div className="gss-toolbar">
        <div style={{ fontWeight: 600 }}>Rattachement des salariés</div>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <input type="checkbox" checked={sansPopulation}
            onChange={(e) => { setSansPopulation(e.target.checked); setSelection([]); }} />
          N’afficher que les salariés sans population
        </label>
        {/* ═══════════════════════════════════════════════════════════════════
            LA SÉLECTION EN MASSE  *(file 123)*
            ═══════════════════════════════════════════════════════════════════
            ⚠️ « TOUT SÉLECTIONNER » PORTE SUR LA LISTE AFFICHÉE, PAS SUR
               L'EFFECTIF. C'est tout l'intérêt du filtre juste à gauche : sans
               cela, cocher « sans population » puis « tout sélectionner »
               rattacherait aussi ceux qui ont déjà une population. *Un bouton
               qui ignore le filtre au-dessus de lui transforme un tri en
               piège.*
            ⓘ Le COMPTEUR est permanent, et il dit les deux nombres. Le bouton
              « Rattacher » disait déjà le total de la sélection — mais
              seulement une fois non vide, et sans dire sur combien.
            ⓘ 97 agents = 97 clics avant ce lot. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
          <span style={{ fontSize: 13, color: 'var(--gss-text-muted, #777)' }}>
            {selection.length} sélectionné{selection.length > 1 ? 's' : ''} sur {liste.length} affiché{liste.length > 1 ? 's' : ''}
          </span>
          <button className="gss-btn" style={{ fontSize: 12 }}
            disabled={liste.length === 0 || selection.length === liste.length}
            onClick={() => setSelection(liste.map((a) => a.id))}>
            Tout sélectionner
          </button>
          <button className="gss-btn" style={{ fontSize: 12 }}
            disabled={selection.length === 0}
            onClick={() => setSelection([])}>
            Tout décocher
          </button>
        </div>
      </div>

      {bilan && (
        <div style={{
          border: `1px solid ${bilan.refus ? '#b71c1c' : '#2e7d32'}`,
          background: bilan.refus ? '#fdecea' : '#edf7ed',
          borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: 13, marginBottom: '1rem',
        }}>
          {bilan.message}
          {/* ⚠️ LES NON RATTACHÉS SONT NOMMÉS, UN PAR UN, AVEC LEUR RAISON.
              ⓘ Avant C2b la route ne rendait que des identifiants : l'écran
                affichait une liste d'UUID, qui disait « quelque chose a échoué »
                sans dire quoi. Le serveur rend désormais une phrase par refus —
                « occupe déjà ce poste », « déjà sorti de l'effectif » — et c'est
                celle-ci qui s'affiche. *Un UUID n'est pas une raison.* */}
          {bilan.refuses && bilan.refuses.length > 0 && (
            <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: '1.2rem' }}>
              {bilan.refuses.map((r) => (
                <li key={r.id} style={{ marginBottom: 2 }}>{r.message}</li>
              ))}
            </ul>
          )}
          {!bilan.refuses && bilan.non_rattaches && bilan.non_rattaches.length > 0 && (
            <div style={{ marginTop: 4 }}>
              Non rattachés ({bilan.non_rattaches.length}) : {bilan.non_rattaches.join(', ')}
            </div>
          )}
          {/* ⚠️ LES ALERTES SE VOIENT, ET ELLES NE BLOQUENT RIEN (Dom, 13/10) :
              un rattachement au 15 du mois est légitime ; c'est la paie du mois
              qui demande une décision, et la RH doit la lire MAINTENANT. */}
          {bilan.alertes && bilan.alertes.length > 0 && (
            <div style={{ marginTop: 8, borderTop: '1px solid #e0c08a', paddingTop: 6 }}>
              {bilan.alertes.map((a) => (
                <div key={a.code} style={{ color: '#B45309', marginBottom: 2 }}>
                  {a.salaries > 1 ? `${a.salaries} salariés — ` : ''}{a.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {peutEcrire && (
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <select className="gss-input" style={{ width: 'auto', minWidth: 220 }}
            value={cible} onChange={(e) => setCible(e.target.value)}>
            <option value="">— Population de destination —</option>
            {cibles.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
          </select>
          {/* ① LES DEUX CHAMPS DU LOT — le seul changement visible de ce lot. */}
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            À partir du
            <input type="date" className="gss-input" style={{ width: 'auto' }}
              value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} />
          </label>
          <input className="gss-input" style={{ width: 'auto', minWidth: 260 }}
            placeholder="Motif du rattachement (ex. : réorganisation du service)"
            value={motif} onChange={(e) => setMotif(e.target.value)} />
          {/* ⚠️ GRISÉ POUR UNE DONNÉE QUI MANQUE, ET IL DIT LAQUELLE. */}
          <button className="gss-btn gss-btn-primary"
            disabled={!cible || selection.length === 0 || !dateEffet || !motif.trim() || enVol}
            title={!cible ? 'Choisir la population de destination.'
              : (selection.length === 0 ? 'Cocher au moins un salarié.'
                : (!dateEffet ? 'Indiquer la date d’effet du rattachement.'
                  : (!motif.trim() ? 'Indiquer le motif du rattachement.' : '')))}
            onClick={rattacher}>
            Rattacher {selection.length > 0 ? `(${selection.length})` : ''}
          </button>
          {(!cible || selection.length === 0 || !dateEffet || !motif.trim()) && (
            <span style={{ fontSize: 12, color: '#B45309' }}>
              {!cible
                ? 'Choisir la population de destination.'
                : (selection.length === 0
                  ? 'Cocher au moins un salarié.'
                  : (!dateEffet
                    ? 'Indiquer la date d’effet : elle vaut pour tout le lot.'
                    : 'Indiquer le motif : il vaut pour tout le lot.'))}
            </span>
          )}
          {/* ⓘ LA RÈGLE DU LOT SE DIT À L'ÉCRAN, pas seulement dans le refus du
              serveur. Sans cette phrase, un utilisateur qui rattache dix
              salariés changeant à trois dates différentes choisirait une date
              « moyenne » — et personne ne saurait jamais qu'elle est fausse. */}
          <span style={{ fontSize: 12, color: 'var(--gss-text-muted, #777)', flexBasis: '100%' }}>
            La date d’effet et le motif valent pour <strong>tout le lot</strong>. Si les
            salariés changent à des dates différentes, faites plusieurs lots.
          </span>
          {cibles.length === 0 && (
            <span style={{ fontSize: 12, color: '#B45309' }}>
              Aucune population active : déclarez-en une dans l’onglet
              « Populations ».
            </span>
          )}
        </div>
      )}

      <div className="gss-table-wrap">
        <table className="gss-table">
          <thead>
            <tr>
              {peutEcrire && <th style={{ width: 36 }}></th>}
              <th>Matricule</th><th>Nom</th><th>Population</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((a) => (
              <tr key={a.id}>
                {peutEcrire && (
                  <td>
                    <input type="checkbox" checked={selection.includes(a.id)}
                      onChange={(e) => setSelection((s) => (e.target.checked
                        ? s.concat([a.id]) : s.filter((x) => x !== a.id)))} />
                  </td>
                )}
                <td><strong>{a.matricule}</strong></td>
                <td>{a.prenom} {a.nom}</td>
                {/* ⛔ « à renseigner », JAMAIS un tiret ni un vide : c'est un
                    acte qui reste à faire, et sans lui la paie refuse. */}
                <td style={{ color: a.population_id ? 'inherit' : '#B45309' }}>
                  {a.population_id
                    ? (parId.get(a.population_id)?.libelle || a.population_id)
                    : A_RENSEIGNER}
                </td>
              </tr>
            ))}
            {liste.length === 0 && (
              <tr>
                <td colSpan={peutEcrire ? 4 : 3} style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-muted, #777)' }}>
                  {sansPopulation
                    ? 'Tous les salariés de l’effectif sont rattachés à une population.'
                    : 'Aucun salarié dans l’effectif.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
